import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const presets = [10, 25, 50, 100];

// Inner form that has access to Stripe context
function CheckoutForm({ amount, playerId, donorName, onSuccess, onError }) {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !ready) return;

    setProcessing(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + "/donation-complete",
      },
      redirect: "if_required",
    });

    if (error) {
      onError(error.message);
      setProcessing(false);
    } else {
      // Redirect to thank you page
      const params = new URLSearchParams({
        donor: donorName || "Friend",
        amount: String(amount),
      });
      navigate(`/thank-you/${playerId}?${params.toString()}`);
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement className="mb-4" onReady={() => setReady(true)} />
      <button
        type="submit"
        disabled={!stripe || !ready || processing}
        className="w-full bg-black hover:bg-neutral-800 text-white font-bold py-3.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-base"
      >
        {processing ? "Processing..." : !ready ? "Loading..." : `Donate $${amount}`}
      </button>
    </form>
  );
}

export default function DonateModal({ playerId, playerName, onClose, onDonated }) {
  const [step, setStep] = useState("info"); // "info" | "payment" | "success"
  const [amount, setAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);

  const effectiveAmount = isCustom ? Number(customAmount) || 0 : amount;

  // Create payment intent when moving to payment step
  const handleContinueToPayment = async (e) => {
    e.preventDefault();
    if (effectiveAmount <= 0) return;
    setLoading(true);
    setError(null);

    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      const res = await fetch(
        `${supabaseUrl}/functions/v1/create-payment-intent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${anonKey}`,
            apikey: anonKey,
          },
          body: JSON.stringify({
            amount: effectiveAmount,
            donor_name: name,
            donor_email: email,
            player_id: playerId || null,
            player_name: playerName || null,
            message: message || null,
            is_anonymous: anonymous,
          }),
        }
      );

      const data = await res.json();
      console.log("Payment intent response:", res.status, data);

      if (!res.ok) {
        throw new Error(data.error || `Server error: ${res.status}`);
      }

      setClientSecret(data.clientSecret);
      setStep("payment");
    } catch (err) {
      setError(err.message || "Failed to initialize payment. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    setStep("success");
    if (onDonated) onDonated();
  };

  // Success screen
  if (step === "success") {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
        <div className="fixed inset-0 bg-navy/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-8 text-center shadow-xl">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="font-display text-2xl font-bold text-navy mb-2">Thank You!</h3>
          <p className="text-navy/60 mb-6">
            Your ${effectiveAmount} donation{playerName ? ` for ${playerName}` : ""} makes a real difference.
          </p>
          <button
            onClick={onClose}
            className="bg-navy text-white font-semibold py-3 px-8 rounded-lg hover:bg-navy-light transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Payment step — Stripe Payment Element
  if (step === "payment" && clientSecret) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
        <div className="fixed inset-0 bg-navy/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
          <div className="sticky top-0 bg-white rounded-t-2xl px-6 pt-6 pb-4 border-b border-warm-gray flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-navy">Payment Details</h2>
              <p className="text-sm text-navy/50 mt-0.5">Donating ${effectiveAmount}{playerName ? ` for ${playerName}` : ""}</p>
            </div>
            <button
              onClick={() => setStep("info")}
              className="text-navy/40 hover:text-navy transition-colors text-sm font-medium"
            >
              Back
            </button>
          </div>
          <div className="p-6">
            {error && (
              <p className="text-sm text-red-600 text-center bg-red-50 rounded-lg py-2 mb-4">
                {error}
              </p>
            )}
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: "stripe",
                  variables: {
                    colorPrimary: "#1a1a2e",
                    borderRadius: "8px",
                  },
                },
              }}
            >
              <CheckoutForm
                amount={effectiveAmount}
                playerId={playerId}
                donorName={anonymous ? "Friend" : name}
                onSuccess={handlePaymentSuccess}
                onError={(msg) => setError(msg)}
              />
            </Elements>
            <p className="text-xs text-navy/40 text-center mt-4">
              Your donation is made to{" "}
              <a
                href="https://PSPeople.org"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-navy/60"
              >
                Providence Serving People
              </a>
              , a nonprofit organization.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Info step — donor details & amount
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="fixed inset-0 bg-navy/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-2xl px-6 pt-6 pb-4 border-b border-warm-gray flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-navy">
            {playerName ? `Donate for ${playerName}` : "Donate to the Team"}
          </h2>
          <button
            onClick={onClose}
            className="text-navy/40 hover:text-navy transition-colors p-1"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleContinueToPayment} className="p-6 space-y-5">
          {/* Amount selector */}
          <div>
            <label className="block text-sm font-semibold text-navy mb-2">
              Donation Amount
            </label>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => { setAmount(p); setIsCustom(false); }}
                  className={`py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
                    !isCustom && amount === p
                      ? "bg-navy text-white border-navy"
                      : "bg-white text-navy border-warm-gray hover:border-navy/30"
                  }`}
                >
                  ${p}
                </button>
              ))}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40 font-semibold">$</span>
              <input
                type="number"
                min="1"
                placeholder="Custom amount"
                value={customAmount}
                onFocus={() => setIsCustom(true)}
                onChange={(e) => { setCustomAmount(e.target.value); setIsCustom(true); }}
                className="w-full pl-7 pr-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">
              Your Name
            </label>
            <input
              type="text"
              required={!anonymous}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={anonymous}
              placeholder={anonymous ? "Donating anonymously" : "Full name"}
              className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black disabled:bg-warm-gray disabled:text-navy/40"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">
              Dedication Message{" "}
              <span className="font-normal text-navy/40">(optional)</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              placeholder="Leave an encouraging message..."
              className="w-full px-4 py-2.5 border border-warm-gray rounded-lg text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black resize-none"
            />
          </div>

          {/* Anonymous toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              className={`w-10 h-6 rounded-full relative transition-colors ${
                anonymous ? "bg-black" : "bg-warm-gray"
              }`}
              onClick={() => setAnonymous(!anonymous)}
            >
              <div
                className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                  anonymous ? "translate-x-[18px]" : "translate-x-0.5"
                }`}
              />
            </div>
            <span className="text-sm text-navy">Donate anonymously</span>
          </label>

          {/* Fee transparency */}
          <p className="text-xs text-navy/40 text-center">
            10% supports coaching & program administration
          </p>

          {/* PSP nonprofit disclosure */}
          <p className="text-xs text-navy/40 text-center">
            Your donation is made to{" "}
            <a
              href="https://PSPeople.org"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-navy/60"
            >
              Providence Serving People
            </a>
            , a nonprofit organization.
          </p>

          {error && (
            <p className="text-sm text-red-600 text-center bg-red-50 rounded-lg py-2">
              {error}
            </p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={effectiveAmount <= 0 || loading}
            className="w-full bg-black hover:bg-neutral-800 text-white font-bold py-3.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-base"
          >
            {loading ? "Setting up payment..." : `Continue — $${effectiveAmount > 0 ? effectiveAmount : "—"}`}
          </button>
        </form>
      </div>
    </div>
  );
}
